import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  type RedirectRequest,
  type SilentRequest,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback } from "react";

import { clearStaleInteractionStatus } from "./msalStorage";

let redirectInFlight: Promise<null> | null = null;

function isBrowserAuthErrorWithCode(error: unknown, code: string) {
  return error instanceof BrowserAuthError && error.errorCode === code;
}

async function acquireTokenRedirectOnce(instance: ReturnType<typeof useMsal>["instance"], request: RedirectRequest) {
  if (redirectInFlight) {
    return redirectInFlight;
  }

  redirectInFlight = (async () => {
    try {
      await instance.acquireTokenRedirect(request);
      return null;
    } finally {
      redirectInFlight = null;
    }
  })();

  return redirectInFlight;
}

export function useAccessToken() {
  const { instance, accounts } = useMsal();

  const getToken = useCallback(
    async (scopes: string[] = ["User.Read"]) => {
      const account = instance.getActiveAccount() ?? accounts[0];
      const request: SilentRequest = {
        scopes,
        account,
      };
      const redirectRequest: RedirectRequest = {
        scopes,
        ...(account ? { account } : {}),
      };

      try {
        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
      } catch (error) {
        if (isBrowserAuthErrorWithCode(error, BrowserAuthErrorCodes.interactionInProgress)) {
          if (redirectInFlight) {
            await redirectInFlight;
            return null;
          }

          try {
            const forcedRetry = await instance.acquireTokenSilent({ ...request, forceRefresh: true });
            return forcedRetry.accessToken;
          } catch (retryError) {
            if (retryError instanceof InteractionRequiredAuthError) {
              await acquireTokenRedirectOnce(instance, redirectRequest);
              return null;
            }
            throw retryError;
          }
        }

        if (isBrowserAuthErrorWithCode(error, BrowserAuthErrorCodes.timedOut)) {
          clearStaleInteractionStatus(instance.getConfiguration().auth.clientId);
          await instance.clearCache({ account });

          try {
            const retryResponse = await instance.acquireTokenSilent({ ...request, forceRefresh: true });
            return retryResponse.accessToken;
          } catch (retryError) {
            if (retryError instanceof InteractionRequiredAuthError) {
              await acquireTokenRedirectOnce(instance, redirectRequest);
              return null;
            }
            throw retryError;
          }
        }

        if (error instanceof InteractionRequiredAuthError) {
          try {
            await acquireTokenRedirectOnce(instance, redirectRequest);
            return null;
          } catch (redirectError) {
            console.error("Redirect error", redirectError);
            throw redirectError;
          }
        }
        throw error; // Rethrow other network/config errors
      }
    },
    [instance, accounts],
  );

  return getToken;
}
